import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  Grid,
  Card,
  CardContent,
  Stack,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Switch,
  FormGroup,
  Select,
  MenuItem,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import { useCustom, useNotification, useCustomMutation } from '@refinedev/core'
import { useAuth0 } from '@/utils/auth/auth0'
import { getTierLabel } from '../utils/tierUtils'

interface OrganisationReconciliation {
  org_uuid: string
  organisation_name: string
  payment_type: string
  billing_amount: number | null
  membership: string | null
  last_sync_attempt: Date | null
  sync_status: string
  assigned_user_count: number
  potential_matches_count: number
}

interface XeroCustomer {
  contact_id: string
  contact_name: string
  email: string
  contact_status: string
  created_at: string
  updated_at: string
}

interface XeroCustomerFinancials {
  contact_id: string
  contact_name: string
  email_addresses: string[]
  invoice_summary: {
    total_invoices: number
    total_amount: number
    paid_amount: number
    outstanding_amount: number
    last_invoice_date: string | null
  }
  payment_summary: {
    total_payments: number
    last_payment_date: string | null
    payment_methods: string
  }
  recurring_invoices: {
    active_templates: number
    next_scheduled_date: string | null
    template_amount: number
    schedule_frequency: string | null
  }
}

interface XeroReconciliationModalProps {
  open: boolean
  onClose: () => void
  organisation: OrganisationReconciliation
  onComplete: () => void
}

export const XeroReconciliationModal: React.FC<
  XeroReconciliationModalProps
> = ({ open, onClose, organisation, onComplete }) => {
  const [customers, setCustomers] = useState<XeroCustomer[]>([])
  const [filteredCustomers, setFilteredCustomers] = useState<XeroCustomer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [customerFinancials, setCustomerFinancials] = useState<
    Record<string, XeroCustomerFinancials>
  >({})
  const [loading, setLoading] = useState(false)
  const [customersLoading, setCustomersLoading] = useState(true)
  const [orgAccount, setOrgAccount] = useState<any>(null)
  const [accountLoading, setAccountLoading] = useState(false)
  const [editingBilling, setEditingBilling] = useState(false)
  const [billingFrequency, setBillingFrequency] = useState<string>('')
  const [billingAmount, setBillingAmount] = useState<number | null>(0)
  const [invoiceDetails, setInvoiceDetails] = useState<any>(null)
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('')
  const [selectedOrgUuid, setSelectedOrgUuid] = useState<string>('')

  const { open: openNotification } = useNotification()
  const { getAccessTokenSilently } = useAuth0()

  // Fetch unassigned Xero customers
  const { refetch: fetchCustomers } = useCustom({
    url: '/xero/reconciliation/customers',
    method: 'get',
    queryOptions: {
      enabled: false,
      onSuccess: (data: any) => {
        setCustomers(data.data)
        setFilteredCustomers(data.data)
        setCustomersLoading(false)
      },
      onError: () => {
        openNotification?.({
          message: 'Failed to load Xero customers',
          type: 'error',
        })
        setCustomersLoading(false)
      },
    },
  })

  // We'll use a separate useCustom for each financial data fetch

  // Fetch invoice details
  const { refetch: fetchInvoiceDetailsApi } = useCustom({
    url: selectedCustomerId
      ? `/xero/customer/${selectedCustomerId}/latest-invoice`
      : '',
    method: 'get',
    queryOptions: {
      enabled: false,
      onSuccess: (data: any) => {
        setInvoiceDetails(data.data)
        setInvoiceLoading(false)
      },
      onError: (error: any) => {
        setInvoiceLoading(false)
        // It's ok if there's no invoice
      },
    },
  })

  const fetchInvoiceDetails = (customerId: string) => {
    setInvoiceLoading(true)
    setInvoiceDetails(null)
    setSelectedCustomerId(customerId)
    // Small delay to ensure state is updated before refetch
    setTimeout(() => fetchInvoiceDetailsApi(), 0)
  }

  const fetchOrgAccount = async (orgUuid: string) => {
    if (!orgUuid || orgUuid === 'undefined' || orgUuid === 'null') {
      setAccountLoading(false)
      setOrgAccount(null)
      openNotification?.({
        message: 'Invalid organisation UUID provided',
        type: 'error',
      })
      return
    }

    setAccountLoading(true)

    try {
      const token = await getAccessTokenSilently()
      const response = await fetch(`${import.meta.env.VITE_API_URL}/orgs/account/${orgUuid}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      const accountData = {
        ...data,
        name: organisation.organisation_name,
      }

      setOrgAccount(accountData)
      setBillingFrequency(data.billing_frequency || 'Monthly')
      setBillingAmount(
        data.billing_amount !== null ? parseFloat(data.billing_amount) : null
      )
      setAccountLoading(false)
    } catch (error: any) {
      setAccountLoading(false)
      setOrgAccount(null)
      openNotification?.({
        message: `Failed to load organisation account details: ${error?.message || 'Unknown error'}`,
        type: 'error',
      })
    }
  }

  // Update billing
  const { mutate: updateBilling } = useCustomMutation()

  const handleUpdateBilling = async () => {
    if (!organisation.org_uuid || !orgAccount) return

    const updateData: any = {
      org_uuid: organisation.org_uuid,
      membership: orgAccount.membership,
      membership_until: orgAccount.membership_until,
      tier: orgAccount.tier,
      payment_type: orgAccount.payment_type,
      billing_frequency: billingFrequency,
      billing_amount: billingAmount,
    }

    updateBilling(
      {
        url: `/orgs/account/${organisation.org_uuid}`,
        method: 'patch',
        values: updateData,
      },
      {
        onSuccess: async () => {
          openNotification?.({
            message: 'Billing details updated successfully',
            type: 'success',
          })
          setEditingBilling(false)
          // Refresh the account data
          fetchOrgAccount(organisation.org_uuid)
        },
        onError: () => {
          openNotification?.({
            message: 'Failed to update billing details',
            type: 'error',
          })
        },
      }
    )
  }

  // Execute assignment
  const { mutate: executeAssignment } = useCustomMutation()

  useEffect(() => {
    if (open) {
      fetchCustomers()
      setSelectedCustomer('')
      setSearchTerm('')
      setOrgAccount(null)
      setEditingBilling(false)
      setInvoiceDetails(null)
      setInvoiceLoading(false)

      // Fetch organisation account details
      if (organisation?.org_uuid) {
        fetchOrgAccount(organisation.org_uuid)
      } else {
        openNotification?.({
          message: 'Organisation UUID is missing',
          type: 'error',
        })
      }
    }
  }, [open, fetchCustomers, organisation])

  const isLikelyMatch = (customerName: string, orgName: string): boolean => {
    const cleanCustomer = customerName.toLowerCase().trim()
    const cleanOrg = orgName.toLowerCase().trim()

    // Check if customer name contains org name or vice versa
    return cleanCustomer.includes(cleanOrg) || cleanOrg.includes(cleanCustomer)
  }

  useEffect(() => {
    let filtered = customers

    if (searchTerm) {
      const trimmedSearch = searchTerm.trim().toLowerCase()
      filtered = customers.filter(
        (customer) =>
          customer.contact_name
            .toLowerCase()
            .includes(trimmedSearch) ||
          customer.email?.toLowerCase().includes(trimmedSearch)
      )
    }

    // Sort customers: potential matches first, then alphabetically
    const sorted = filtered.sort((a, b) => {
      const aMatch = isLikelyMatch(
        a.contact_name,
        organisation.organisation_name
      )
      const bMatch = isLikelyMatch(
        b.contact_name,
        organisation.organisation_name
      )

      // If one is a match and the other isn't, prioritize the match
      if (aMatch && !bMatch) return -1
      if (!aMatch && bMatch) return 1

      // If both are matches or both aren't matches, sort alphabetically
      return a.contact_name.localeCompare(b.contact_name)
    })

    setFilteredCustomers(sorted)
  }, [searchTerm, customers, organisation.organisation_name])

  const handleCustomerSelect = (customerId: string) => {
    setSelectedCustomer(customerId)

    // Fetch invoice details for this customer
    fetchInvoiceDetails(customerId)

    // Fetch financial data if not already loaded
    if (!customerFinancials[customerId]) {
      // Use fetch API directly for this GET request
      fetch(`/v1/xero/reconciliation/customers/${customerId}/financial-data`, {
        headers: {
          Authorization: 'Bearer admin-token',
          'Content-Type': 'application/json',
        },
      })
        .then((response) => response.json())
        .then((data) => {
          setCustomerFinancials((prev) => ({
            ...prev,
            [customerId]: data,
          }))
        })
        .catch((error) => {
          console.error(
            `Failed to fetch financial data for ${customerId}:`,
            error
          )
        })
    }
  }

  const handleExecuteAssignment = () => {
    if (!selectedCustomer) return

    setLoading(true)
    executeAssignment(
      {
        url: '/xero/reconciliation/assign',
        method: 'post',
        values: {
          org_uuid: organisation.org_uuid,
          xero_contact_id: selectedCustomer,
          create_historical_records: true, // Always create historical records
        },
      },
      {
        onSuccess: (data: any) => {
          const customer = filteredCustomers.find(
            (c) => c.contact_id === selectedCustomer
          )
          openNotification?.({
            message: `Successfully linked ${
              organisation.organisation_name
            } to ${customer?.contact_name || 'Xero customer'}`,
            type: 'success',
          })
          setLoading(false)
          onComplete()
        },
        onError: () => {
          openNotification?.({
            message: 'Failed to link Xero customer to organisation',
            type: 'error',
          })
          setLoading(false)
        },
      }
    )
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
    }).format(amount)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString()
  }

  const renderCustomerSelection = () => (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select Xero Customer for {organisation.organisation_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Choose an unassigned Xero customer to link with this organisation.
        </Typography>
      </Box>

      {/* Organisation Summary */}
      {orgAccount && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <BusinessIcon color="primary" />
              <Typography variant="h6">Selected Organisation</Typography>
              {orgAccount && !editingBilling && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setEditingBilling(true)}
                  size="small"
                  sx={{ ml: 'auto' }}
                >
                  Edit Billing
                </Button>
              )}
            </Stack>

            {accountLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : orgAccount ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Organisation Info
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {orgAccount.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Tier:</strong> {getTierLabel(orgAccount.tier, 'Not set')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Membership:</strong>{' '}
                    {orgAccount.membership || 'Not set'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Billing Details
                  </Typography>
                  <Typography variant="body2">
                    <strong>Payment Type:</strong>{' '}
                    {orgAccount.payment_type || 'Not set'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Amount:</strong>{' '}
                    {orgAccount.billing_amount !== null
                      ? formatCurrency(orgAccount.billing_amount)
                      : 'Not set'}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">
                      <strong>Frequency:</strong>
                    </Typography>
                    {editingBilling ? (
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={billingFrequency}
                          onChange={(e) => setBillingFrequency(e.target.value)}
                        >
                          <MenuItem value="Monthly">Monthly</MenuItem>
                          <MenuItem value="Quarterly">Quarterly</MenuItem>
                          <MenuItem value="Yearly">Yearly</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body2">
                        {orgAccount.billing_frequency || 'Not set'}
                      </Typography>
                    )}
                  </Stack>
                  {editingBilling && (
                    <Box sx={{ mt: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">
                          <strong>Amount:</strong>
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={billingAmount === null ? '' : billingAmount}
                          onChange={(e) => {
                            const value = e.target.value
                            if (value === '') {
                              setBillingAmount(null)
                            } else {
                              const numValue = Number(value)
                              setBillingAmount(numValue)
                            }
                          }}
                          sx={{ width: 120 }}
                          inputProps={{
                            step: 0.01,
                          }}
                          placeholder="Enter amount or leave empty"
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        Leave blank to use default tier pricing
                      </Typography>
                    </Box>
                  )}
                </Grid>
                {editingBilling && (
                  <Grid item xs={12} md={4}>
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <Button
                        startIcon={<SaveIcon />}
                        onClick={handleUpdateBilling}
                        size="small"
                        variant="contained"
                      >
                        Save
                      </Button>
                      <Button
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          setEditingBilling(false)
                          setBillingFrequency(
                            orgAccount.billing_frequency || 'Monthly'
                          )
                          setBillingAmount(
                            orgAccount.billing_amount !== null
                              ? parseFloat(orgAccount.billing_amount.toString())
                              : null
                          )
                        }}
                        size="small"
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Alert severity="info">Loading organisation details...</Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Latest Invoice Details */}
      {selectedCustomer && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <ReceiptIcon color="primary" />
              <Typography variant="h6">Latest Invoice Details</Typography>
            </Stack>
            {invoiceLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : invoiceDetails ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Invoice Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Invoice #:</strong> {invoiceDetails.invoice_number}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date:</strong>{' '}
                    {invoiceDetails.date_issued
                      ? new Date(
                          invoiceDetails.date_issued
                        ).toLocaleDateString()
                      : 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong>{' '}
                    <Chip
                      size="small"
                      label={invoiceDetails.status}
                      color="primary"
                    />
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total:</strong>{' '}
                    {formatCurrency(invoiceDetails.total)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Line Items
                  </Typography>
                  {invoiceDetails.line_items &&
                    invoiceDetails.line_items.map(
                      (item: any, index: number) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 1,
                            p: 1,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            {item.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.quantity} × {formatCurrency(item.unit_amount)}{' '}
                            = {formatCurrency(item.line_amount)}
                          </Typography>
                        </Box>
                      )
                    )}
                </Grid>
              </Grid>
            ) : (
              <Alert severity="info">
                No invoice details available for this customer
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="Search customers..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
        }}
        sx={{ mb: 2 }}
      />

      {customersLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Customer Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Financial Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.map((customer) => {
                const financials = customerFinancials[customer.contact_id]
                const isPotentialMatch = isLikelyMatch(
                  customer.contact_name,
                  organisation.organisation_name
                )
                return (
                  <TableRow
                    key={customer.contact_id}
                    hover
                    selected={selectedCustomer === customer.contact_id}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isPotentialMatch
                        ? 'action.hover'
                        : 'inherit',
                    }}
                    onClick={() => handleCustomerSelect(customer.contact_id)}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {customer.contact_name}
                        </Typography>
                        {isPotentialMatch && (
                          <Chip
                            size="small"
                            label="Potential Match"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{customer.email || 'No email'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={customer.contact_status}
                        color={
                          customer.contact_status === 'ACTIVE'
                            ? 'success'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {financials ? (
                        <Stack spacing={0.5}>
                          <Typography variant="caption">
                            {financials.invoice_summary.total_invoices} invoices
                          </Typography>
                          <Typography variant="caption">
                            {formatCurrency(
                              financials.invoice_summary.total_amount
                            )}{' '}
                            total
                          </Typography>
                          {financials.recurring_invoices.active_templates >
                            0 && (
                            <Typography variant="caption" color="primary">
                              {financials.recurring_invoices.active_templates}{' '}
                              recurring
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Click to load details
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedCustomer && customerFinancials[selectedCustomer] && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Financial Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <ReceiptIcon color="primary" />
                    <Typography variant="subtitle2">Invoices</Typography>
                  </Stack>
                  <Typography variant="h6">
                    {
                      customerFinancials[selectedCustomer].invoice_summary
                        .total_invoices
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total:{' '}
                    {formatCurrency(
                      customerFinancials[selectedCustomer].invoice_summary
                        .total_amount
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Outstanding:{' '}
                    {formatCurrency(
                      customerFinancials[selectedCustomer].invoice_summary
                        .outstanding_amount
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <PaymentIcon color="success" />
                    <Typography variant="subtitle2">Payments</Typography>
                  </Stack>
                  <Typography variant="h6">
                    {
                      customerFinancials[selectedCustomer].payment_summary
                        .total_payments
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last:{' '}
                    {formatDate(
                      customerFinancials[selectedCustomer].payment_summary
                        .last_payment_date
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Xero Customer Reconciliation</DialogTitle>
      <DialogContent>{renderCustomerSelection()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExecuteAssignment}
          disabled={!selectedCustomer || loading}
          color="primary"
        >
          {loading ? <CircularProgress size={20} /> : 'Link Customer'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
